/**
 * Query relevance filter utility to filter and score search results by relevance to the query.
 * 
 * Ported from backend Python implementation for client-side filtering to improve API performance.
 */

export interface ScoredListing<T> {
  listing: T;
  score: number;
}

/**
 * Service that filters and scores search results by relevance to the query.
 * 
 * Uses keyword matching, fuzzy matching, and scoring to identify relevant results.
 */
export class QueryRelevanceFilter {
  private minRelevanceScore: number;
  private wordPattern: RegExp;
  private normalizePattern: RegExp;

  constructor(minRelevanceScore: number = 0.3) {
    /**
     * Initialize the query relevance filter.
     * 
     * @param minRelevanceScore - Minimum relevance score (0.0-1.0) to include a result.
     *                            Lower values = more lenient, higher = more strict.
     */
    this.minRelevanceScore = minRelevanceScore;
    // Cache compiled regex patterns for performance
    this.normalizePattern = /[^a-z0-9]/g;
    this.wordPattern = /\b\w+\b/g;
  }

  /**
   * Filter listings by relevance to query and return scored results.
   * 
   * @param listings - List of product listings to filter
   * @param query - Search query string
   * @returns List of tuples (listing, relevance_score) sorted by relevance (highest first)
   */
  filterAndScore<T extends { title?: string | null; description?: string | null }>(
    listings: T[],
    query: string
  ): ScoredListing<T>[] {
    if (!query || !listings.length) {
      return listings.map(listing => ({ listing, score: 0.0 }));
    }

    const queryLower = query.toLowerCase().trim();
    const queryWords = this.extractKeywords(queryLower);
    const queryWordsSet = new Set(queryWords); // For faster membership checks

    // Pre-compute query normalization once
    const queryNormalized = queryLower.replace(this.normalizePattern, '');

    // Process all listings in one pass
    const scoredListings: ScoredListing<T>[] = listings
      .map(listing => {
        const score = this.calculateRelevanceScoreFast(
          listing,
          queryLower,
          queryWords,
          queryWordsSet,
          queryNormalized
        );
        return { listing, score };
      })
      .filter(({ score }) => score >= this.minRelevanceScore);

    // Sort by relevance score (highest first)
    scoredListings.sort((a, b) => b.score - a.score);

    return scoredListings;
  }

  /**
   * Extract meaningful keywords from query.
   * 
   * Removes common stop words and extracts important terms.
   */
  private extractKeywords(query: string): string[] {
    // Common stop words to ignore
    const stopWords = new Set([
      'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
      'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
      'to', 'was', 'will', 'with', 'the', 'for', 'car', 'cars', 'vehicle', 'vehicles'
    ]);

    const words = query.match(this.wordPattern) || [];
    const keywords = words.filter(w => !stopWords.has(w) && w.length > 2);

    return keywords.length > 0 ? keywords : words; // Fallback to all words if no keywords
  }

  /**
   * Optimized relevance score calculation with pre-computed values.
   * 
   * Scoring factors:
   * 1. Exact query match in title (highest weight)
   * 2. All query words present in title
   * 3. Query words in description
   * 4. Fuzzy string matching (optimized)
   * 5. Partial matches
   */
  private calculateRelevanceScoreFast<T extends { title?: string | null; description?: string | null }>(
    listing: T,
    queryLower: string,
    queryWords: string[],
    queryWordsSet: Set<string>,
    queryNormalized: string
  ): number {
    const title = (listing.title || '').toLowerCase();
    const description = (listing.description || '').toLowerCase();

    // If title/description is empty, very low score
    if (!title) {
      return 0.0;
    }

    let score = 0.0;
    let maxScore = 0.0;

    // 1. Exact query match in title (weight: 1.0) - fastest check first
    if (title.includes(queryLower)) {
      score += 1.0;
      maxScore += 1.0;
    } else {
      maxScore += 1.0;
    }

    // 2. All query words present in title (weight: 0.8) - use set for O(1) lookup
    if (queryWordsSet.size > 0) {
      const wordsInTitle = queryWords.filter(word => title.includes(word)).length;
      const wordMatchRatio = wordsInTitle / queryWords.length;
      score += wordMatchRatio * 0.8;
      maxScore += 0.8;
    }

    // 3. Query words in description (weight: 0.3) - only if description exists
    if (description && queryWordsSet.size > 0) {
      const wordsInDesc = queryWords.filter(word => description.includes(word)).length;
      const descMatchRatio = wordsInDesc / queryWords.length;
      score += descMatchRatio * 0.3;
      maxScore += 0.3;
    }

    // 4. Fuzzy string matching for title (weight: 0.5) - skip if exact match found
    // Only compute if we don't already have a high score (early exit optimization)
    if (score < 1.5 && title.length <= queryLower.length * 3) {
      // Avoid very long titles
      const titleSnippet = title.substring(0, queryLower.length * 2);
      if (queryLower.length <= 50) {
        // Only fuzzy match for shorter queries
        const similarity = this.quickSimilarity(queryLower, titleSnippet);
        score += similarity * 0.5;
        maxScore += 0.5;
      } else {
        // For long queries, use simpler substring check
        if (title.includes(queryLower.substring(0, 20))) {
          score += 0.5;
        }
        maxScore += 0.5;
      }
    } else {
      maxScore += 0.5;
    }

    // 5. Check for partial matches (e.g., "370z" matches "370Z")
    // Use cached regex pattern and pre-computed query_normalized
    const titleNormalized = title.replace(this.normalizePattern, '');

    if (titleNormalized.includes(queryNormalized) || queryNormalized.includes(titleNormalized)) {
      score += 0.3;
      maxScore += 0.3;
    } else {
      maxScore += 0.3;
    }

    // Normalize score to 0.0-1.0 range
    if (maxScore > 0) {
      return Math.min(score / maxScore, 1.0);
    } else {
      return 0.0;
    }
  }

  /**
   * Quick similarity check using character matching.
   * Simpler than full Levenshtein distance but faster.
   */
  private quickSimilarity(s1: string, s2: string): number {
    const minLen = Math.min(s1.length, s2.length);
    if (minLen === 0) return 0;

    let matches = 0;
    for (let i = 0; i < minLen; i++) {
      if (s1[i] === s2[i]) {
        matches++;
      }
    }

    // Also check for substring matches
    const substringMatch = s1.includes(s2) || s2.includes(s1);
    const baseSimilarity = matches / minLen;
    
    return substringMatch ? Math.max(baseSimilarity, 0.7) : baseSimilarity;
  }

  /**
   * Filter listings by relevance and return only relevant ones (sorted by relevance).
   * 
   * Convenience method that returns only listings, not scores.
   */
  filterListings<T extends { title?: string | null; description?: string | null }>(
    listings: T[],
    query: string
  ): T[] {
    const scored = this.filterAndScore(listings, query);
    return scored.map(({ listing }) => listing);
  }
}

